import { useEffect, useState } from "react";
import { SpaceSyncMultiplexer } from "../spaceSyncMultiplexer";

/**
 * Owns the lifecycle of a SpaceSyncMultiplexer keyed off the sync URI.
 *
 * Input:  syncUri (or null when the space isn't ready/active yet)
 * Output: live SpaceSyncMultiplexer, or null when no URI
 *
 * Owned via useState/useEffect rather than useMemo: useMemo doesn't guarantee
 * stable identity (React is allowed to drop the cache), so a useMemo'd WS
 * instance can leak — a new SpaceSyncMultiplexer would be constructed without
 * the previous one's close() ever firing. useEffect's cleanup is the only
 * place we can reliably tear down the WS when the URI changes or we unmount.
 */
export function useSyncMultiplexer(
  syncUri: string | null,
): SpaceSyncMultiplexer | null {
  const [mux, setMux] = useState<SpaceSyncMultiplexer | null>(null);
  useEffect(() => {
    if (!syncUri) {
      setMux(null);
      return;
    }
    const instance = new SpaceSyncMultiplexer(syncUri);
    setMux(instance);
    return () => {
      instance.close();
      setMux(null);
    };
  }, [syncUri]);
  return mux;
}
